import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { Image } from '../Image'
import { List, ListItem } from '../List'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import ConBlur from './assets/con-blur.png'
import ProBlur from './assets/pro-blur.png'
import styles from './ProsCons.module.scss'

export const ProsCons: FunctionComponent<ProsConsProps> = ({
  list,
  type,
  className,
  isOnlyFirstFeatureDisplayed,
  displayCount = true,
  displayHeading = true,
  ...rest
}) => {
  if (!list) {
    return null
  }

  const listToBeMapped = isOnlyFirstFeatureDisplayed ? list.slice(0, 1) : list

  return (
    <div
      className={classnames(styles.prosCons, className)}
      data-testid="sb-pros-cons"
      {...rest}
    >
      {displayHeading && (
        <TypographyV2
          tag="h4"
          textStyle="sb-text-body-default-strong"
          className={classnames('sb-margin-bottom-2', styles.title)}
        >
          {type}
          {displayCount && list.length >= 1 && isOnlyFirstFeatureDisplayed && (
            <TypographyV2
              tag="span"
              textStyle="sb-text-interface-body-small-regular"
              className={styles.count}
              data-testid="sb-count-first-feature-displayed"
            >
              (1 of {list?.length})
            </TypographyV2>
          )}
          {displayCount && list.length >= 1 && !isOnlyFirstFeatureDisplayed && (
            <TypographyV2
              tag="span"
              textStyle="sb-text-interface-body-small-regular"
              className={styles.count}
              data-testid="sb-count"
            >
              ({list.length} of {list.length})
            </TypographyV2>
          )}
        </TypographyV2>
      )}

      {list.length === 0 ? (
        <Image
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          src={type === 'pros' ? ProBlur : ConBlur}
          alt="Content has been blurred out, please log in to view."
          wrapperClassName={styles.image}
        />
      ) : (
        <List>
          {listToBeMapped.map((item: string, index) => (
            <ListItem
              className={classnames(styles.listItem, type && styles[type])}
              key={`${item}-${index}`}
              icon={type === 'pros' ? 'tick' : 'cross'}
            >
              {item}
            </ListItem>
          ))}
        </List>
      )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type ProsConsProps = {
  /**
   *  List array.
   */
  list?: Array<string> | null
  /**
   *  Type of list.
   */
  type?: 'pros' | 'cons'
  /**
   * Boolean to determine whether only the first feature from Pros or Cons is displayed.
   */
  isOnlyFirstFeatureDisplayed?: boolean
  /**
   *  Optional class name.
   */
  className?: string
  /**
   *  Optional prop to hide count
   */
  displayCount?: boolean
  /**
   *  Optional prop to hide heading
   */
  displayHeading?: boolean
} & ComponentPropsWithoutRef<'div'>
